import React, { useState } from 'react';
import { X, Edit2, Plus, Trash2, SkipForward } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
interface EntryRow {
  weight: string;
  sets: string;
  reps: string;
  rest: string;
}
interface CorrectExerciseSheetProps {
  exerciseName: string;
  onSave: () => void;
  onClose: () => void;
  onSkip?: () => void;
}
export function CorrectExerciseSheet({
  exerciseName,
  onSave,
  onClose,
  onSkip
}: CorrectExerciseSheetProps) {
  const [entries, setEntries] = useState<EntryRow[]>([{
    weight: '',
    sets: '',
    reps: '',
    rest: ''
  }]);
  const [showSkipConfirmation, setShowSkipConfirmation] = useState(false);
  const hasMultipleEntries = entries.length > 1;
  const addEntry = () => {
    setEntries([...entries, {
      weight: '',
      sets: '',
      reps: '',
      rest: ''
    }]);
  };
  const removeEntry = (index: number) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== index));
    }
  };
  const updateEntry = (index: number, field: keyof EntryRow, value: string) => {
    setEntries(entries.map((entry, i) => i === index ? {
      ...entry,
      [field]: value
    } : entry));
  };
  const handleSkipConfirm = () => {
    setShowSkipConfirmation(false);
    onSkip?.();
  };
  return <>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end animate-in fade-in duration-200" data-id="element-114">
        <div className="bg-white w-full rounded-t-[2.5rem] p-6 pb-8 shadow-2xl animate-in slide-in-from-bottom-full duration-300 max-h-[85%] flex flex-col" data-id="element-115">
          <div className="flex items-center justify-between mb-2 shrink-0" data-id="element-116">
            <div className="flex items-center gap-3" data-id="element-117">
              <div className="w-10 h-10 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center" data-id="element-118">
                <Edit2 size={20} data-id="element-119" />
              </div>
              <div data-id="element-120">
                <h2 className="text-xl font-extrabold text-slate-800" data-id="element-121">
                  Edit exercise
                </h2>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-122">
              <X size={24} data-id="element-123" />
            </button>
          </div>

          <p className="text-sm font-medium text-slate-500 mb-5 leading-relaxed shrink-0" data-id="element-124">
            {hasMultipleEntries ? <>
                Update the values for{' '}
                <span className="font-bold text-slate-700" data-id="element-125">{exerciseName}</span>{' '}
                across multiple entries.
              </> : <>
                Update the values for{' '}
                <span className="font-bold text-slate-700" data-id="element-126">{exerciseName}</span>
                .
              </>}
          </p>

          {/* Scrollable entries area */}
          <div className="flex-1 overflow-y-auto -mx-1 px-1 mb-5 space-y-4" data-id="element-127">
            {entries.map((entry, index) => <div key={index} className="animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-128">
                {hasMultipleEntries && <div className="flex items-center justify-between mb-2" data-id="element-129">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-130">
                      Entry {index + 1}
                    </span>
                    <button onClick={() => removeEntry(index)} className="flex items-center gap-1 text-xs font-bold text-pink-500 hover:text-pink-600 transition-colors" data-id="element-131">
                      <Trash2 size={12} data-id="element-132" />
                      Remove
                    </button>
                  </div>}
                <div className="grid grid-cols-2 gap-3" data-id="element-133">
                  <Input label="Weight (kg/lbs)" type="number" placeholder="e.g. 60" value={entry.weight} onChange={e => updateEntry(index, 'weight', e.target.value)} data-id="element-134" />
                  <Input label="Sets" type="number" placeholder="e.g. 3" value={entry.sets} onChange={e => updateEntry(index, 'sets', e.target.value)} data-id="element-135" />
                  <Input label="Reps" type="number" placeholder="e.g. 10" value={entry.reps} onChange={e => updateEntry(index, 'reps', e.target.value)} data-id="element-136" />
                  <Input label="Rest (sec)" type="number" placeholder="e.g. 90" value={entry.rest} onChange={e => updateEntry(index, 'rest', e.target.value)} data-id="element-137" />
                </div>
              </div>)}

            {/* Add another entry — subtle in single-entry, standard in multi */}
            {hasMultipleEntries && <div className="border-t border-slate-100 pt-3" data-id="element-138" />}
            <button onClick={addEntry} className={`flex items-center gap-2 text-sm font-bold transition-colors w-full ${hasMultipleEntries ? 'text-blue-500 hover:text-blue-600 pt-0' : 'text-slate-400 hover:text-blue-500 justify-center border border-dashed border-slate-200 hover:border-blue-300 rounded-2xl py-3'}`} data-id="element-139">
              <Plus size={16} data-id="element-140" />
              Add another entry
            </button>
          </div>

          <div className="space-y-3 shrink-0" data-id="element-141">
            <Button variant="primary" fullWidth onClick={onSave} data-id="element-142">
              Save changes
            </Button>
            <Button variant="ghost" fullWidth onClick={onClose} data-id="element-143">
              Cancel
            </Button>

            {/* Skip exercise action */}
            {onSkip && <button onClick={() => setShowSkipConfirmation(true)} className="w-full text-center text-sm font-bold text-slate-500 hover:text-slate-700 py-2 transition-colors" data-id="element-144">
                Skip this exercise for today
              </button>}
          </div>
        </div>
      </div>

      {/* Skip Confirmation Modal */}
      {showSkipConfirmation && <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-145">
          <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl animate-in zoom-in-95 duration-200" data-id="element-146">
            <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4 mx-auto" data-id="element-147">
              <SkipForward size={24} data-id="element-148" />
            </div>

            <h3 className="text-xl font-extrabold text-slate-800 mb-3 text-center" data-id="element-149">
              Skip this exercise?
            </h3>

            <p className="text-slate-600 font-medium text-sm leading-relaxed mb-6 text-center" data-id="element-150">
              This will remove{' '}
              <span className="font-bold text-slate-800" data-id="element-151">{exerciseName}</span>{' '}
              from today's workout only. It won't delete it from your program.
            </p>

            <div className="space-y-3" data-id="element-152">
              <Button variant="primary" fullWidth onClick={handleSkipConfirm} data-id="element-153">
                Skip for today
              </Button>
              <Button variant="ghost" fullWidth onClick={() => setShowSkipConfirmation(false)} data-id="element-154">
                Keep exercise
              </Button>
            </div>
          </div>
        </div>}
    </>;
}